import React from "react";
import { useTranslation } from "react-i18next";
import { Webhook } from "../../types/webhook";

interface WebhookTableProps {
    webhooks: Webhook[];
    isLoading: boolean;
    onEdit: (webhook: Webhook) => void;
    onDelete: (id: number) => void;
    onTest: (id: number) => void;
    onToggleStatus: (id: number) => void;
    onViewEndpoints: (webhookId: number) => void;
    onViewLogs: (webhookId: number, webhookName: string) => void;
    isDeleting: boolean;
    isTesting: boolean;
    isToggling: boolean;
}

const WebhookTable: React.FC<WebhookTableProps> = ({
    webhooks,
    isLoading,
    onEdit,
    onDelete,
    onTest,
    onToggleStatus,
    onViewEndpoints,
    onViewLogs,
    isDeleting,
    isTesting,
    isToggling,
}) => {
    const { t } = useTranslation(["webhooks", "common"]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString();
    };

    if (isLoading) {
        return (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-4">
                    <div className="animate-pulse">
                        <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-1/4 mb-4"></div>
                        <div className="space-y-3">
                            {[...Array(5)].map((_, i) => (
                                <div
                                    key={i}
                                    className="h-12 bg-gray-200 dark:bg-gray-600 rounded"
                                ></div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!webhooks || webhooks.length === 0) {
        return (
            <div className="bg-white dark:bg-gray-800 shadow rounded-lg">
                <div className="px-6 py-8 text-center">
                    <p className="text-gray-500 dark:text-gray-400">
                        {t("webhooks:messages.no_webhooks")}
                    </p>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white dark:bg-gray-800 shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50 dark:bg-gray-900">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.name")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.event_type")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.status")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.success_rate")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.created_at")}
                            </th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                                {t("webhooks:table.actions")}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200">
                        {webhooks.map((webhook) => (
                            <tr
                                key={webhook.id}
                                className="hover:bg-gray-50 dark:bg-gray-900 dark:hover:bg-gray-800 text-gray-900 dark:text-white"
                            >
                                <td className="px-6 py-4 whitespace-wrap">
                                    <div>
                                        <div className="text-sm font-medium text-gray-900 dark:text-white">
                                            {webhook.name}
                                        </div>
                                        <div className="text-sm text-gray-500 dark:text-gray-400">
                                            {webhook.description}
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span className="inline-flex px-2 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                                        {webhook.event_type}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                            webhook.is_active
                                                ? "bg-green-100 text-green-800"
                                                : "bg-red-100 text-red-800"
                                        }`}
                                    >
                                        {webhook.is_active
                                            ? t("webhooks:status.active")
                                            : t("webhooks:status.inactive")}
                                    </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        {(
                                            webhook.stats?.success_rate ?? 0
                                        ).toFixed(1)}
                                        %
                                    </div>
                                    <div className="text-xs text-gray-500 dark:text-gray-400">
                                        {webhook.stats?.success_count ?? 0}/
                                        {webhook.stats?.total_sent ?? 0}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                                    {formatDate(webhook.created_at)}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                    <div className="flex space-x-2">
                                        <button
                                            onClick={() =>
                                                onViewLogs(
                                                    webhook.id,
                                                    webhook.name
                                                )
                                            }
                                            className="text-indigo-600 hover:text-indigo-900"
                                            title={t("webhooks:view_logs")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() =>
                                                onViewEndpoints(webhook.id)
                                            }
                                            className="text-purple-600 hover:text-purple-900"
                                            title={t("webhooks:endpoints.view")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13.828 10.172a4 4 0 010 5.656l-1.414 1.414a4 4 0 01-5.656 0l-1.414-1.414a4 4 0 010-5.656l1.414-1.414m2.828 2.828a4 4 0 010 5.656m4.242-4.242a4 4 0 010-5.656l1.414-1.414a4 4 0 015.656 0l1.414 1.414a4 4 0 010 5.656l-1.414 1.414"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => onEdit(webhook)}
                                            className="text-blue-600 hover:text-blue-900"
                                            title={t("common:edit")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => onTest(webhook.id)}
                                            className="text-green-600 hover:text-green-900"
                                            title={t("webhooks:test_webhook")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() =>
                                                onToggleStatus(webhook.id)
                                            }
                                            className="text-yellow-600 hover:text-yellow-900"
                                            title={t("webhooks:toggle_status")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                                                />
                                            </svg>
                                        </button>
                                        <button
                                            onClick={() => onDelete(webhook.id)}
                                            className="text-red-600 hover:text-red-900"
                                            title={t("common:delete")}
                                            disabled={
                                                isDeleting ||
                                                isTesting ||
                                                isToggling
                                            }
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m-4 0h14"
                                                />
                                            </svg>
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default WebhookTable;
